class SkProductDescription extends HTMLElement {
  #tabs = [];
  #panels = [];
  #activeIndex = 0;
  #abortController = new AbortController();
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    if (this.dataset.enhanced === 'true') return;
    this.dataset.enhanced = 'true';

    const source = this.querySelector('[data-description-source]');
    if (!(source instanceof HTMLElement)) return;

    source.querySelectorAll('img').forEach((image) => {
      image.loading = 'lazy';
      image.decoding = 'async';
    });

    let headings = Array.from(source.querySelectorAll('h2, h3'));
    const prefaceLabel = this.dataset.prefaceLabel?.trim() || 'Description';

    if (headings.length) {
      const preface = this.#extractPreface(source, headings[0]);
      if (preface) {
        const prefaceHeading = document.createElement('h3');
        prefaceHeading.textContent = prefaceLabel;
        source.prepend(prefaceHeading);
        prefaceHeading.after(preface);
        headings = [prefaceHeading, ...headings];
      }
    } else if (source.textContent?.trim() || source.querySelector('img, table, ul, ol, video, iframe')) {
      const descriptionHeading = document.createElement('h3');
      descriptionHeading.textContent = prefaceLabel;
      source.prepend(descriptionHeading);
      headings = [descriptionHeading];
    } else {
      return;
    }
    const layout = document.createElement('div');
    const tabList = document.createElement('div');
    const panelList = document.createElement('div');
    layout.className = 'skpd__tab-layout';
    tabList.className = 'skpd__tabs';
    tabList.setAttribute('role', 'tablist');
    tabList.setAttribute('aria-label', 'Product information');
    panelList.className = 'skpd__panels';

    headings.forEach((heading, index) => {
      if (!heading.isConnected || !source.contains(heading)) return;
      const nextHeading = headings.slice(index + 1).find((candidate) => candidate.isConnected && source.contains(candidate));
      const range = document.createRange();
      range.setStartBefore(heading);

      if (nextHeading) range.setEndBefore(nextHeading);
      else if (source.lastChild) range.setEndAfter(source.lastChild);
      else return;

      const panel = document.createElement('article');
      panel.append(range.extractContents());

      const panelHeading = panel.querySelector('h2, h3');
      const label = panelHeading?.textContent?.trim() || `Section ${index + 1}`;
      const uniqueId = `${this.dataset.sectionId || 'product-description'}-${index + 1}`;
      const tabId = `skpd-tab-${uniqueId}`;
      const panelId = `skpd-panel-${uniqueId}`;

      if (panelHeading) panelHeading.classList.add('skpd__panel-heading');

      panel.classList.add('skpd__panel');
      panel.id = panelId;
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tabId);
      panel.tabIndex = 0;
      panel.hidden = index !== 0;

      const tab = document.createElement('button');
      tab.className = 'skpd__tab';
      tab.type = 'button';
      tab.id = tabId;
      tab.dataset.tabIndex = String(index);
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panelId);
      tab.setAttribute('aria-selected', String(index === 0));
      tab.tabIndex = index === 0 ? 0 : -1;

      const number = document.createElement('span');
      number.className = 'skpd__tab-number';
      number.setAttribute('aria-hidden', 'true');
      number.textContent = String(index + 1).padStart(2, '0');

      const divider = document.createElement('span');
      divider.className = 'skpd__tab-divider';
      divider.setAttribute('aria-hidden', 'true');

      const tabLabel = document.createElement('span');
      tabLabel.className = 'skpd__tab-label';
      tabLabel.textContent = label;

      tab.append(number, divider, tabLabel);
      tabList.append(tab);
      panelList.append(panel);
    });

    if (!tabList.children.length) return;
    layout.append(tabList, panelList);
    source.replaceChildren(layout);

    this.#tabs = Array.from(tabList.querySelectorAll('[role="tab"]'));
    this.#panels = Array.from(panelList.querySelectorAll('[role="tabpanel"]'));

    const { signal } = this.#abortController;
    tabList.addEventListener('click', this.#handleClick, { signal });
    tabList.addEventListener('keydown', this.#handleKeydown, { signal });
    this.classList.add('is-enhanced');
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const tab = event.target.closest('[role="tab"]');
    const index = this.#tabs.indexOf(tab);
    if (index >= 0) this.#selectTab(index, true);
  };

  #handleKeydown = (event) => {
    if (!(event.target instanceof Element)) return;
    const currentTab = event.target.closest('[role="tab"]');
    const currentIndex = this.#tabs.indexOf(currentTab);
    if (currentIndex < 0) return;

    let nextIndex;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % this.#tabs.length;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + this.#tabs.length) % this.#tabs.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = this.#tabs.length - 1;
    else return;

    event.preventDefault();
    this.#selectTab(nextIndex, true);
    this.#tabs[nextIndex]?.focus();
  };

  #selectTab(index, userInitiated) {
    const nextTab = this.#tabs[index];
    const nextPanel = this.#panels[index];
    if (!nextTab || !nextPanel) return;

    this.#tabs.forEach((tab, tabIndex) => {
      const active = tabIndex === index;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });

    this.#panels.forEach((panel, panelIndex) => {
      panel.hidden = panelIndex !== index;
      panel.classList.remove('is-entering');
    });

    this.#activeIndex = index;
    if (!this.#reducedMotion.matches) requestAnimationFrame(() => nextPanel.classList.add('is-entering'));

    if (userInitiated && window.matchMedia('(max-width: 749px)').matches) {
      nextTab.scrollIntoView({
        behavior: this.#reducedMotion.matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }

  #extractPreface(source, firstHeading) {
    if (!source.firstChild || source.firstChild === firstHeading) return null;

    const range = document.createRange();
    range.setStartBefore(source.firstChild);
    range.setEndBefore(firstHeading);
    const fragment = range.extractContents();
    const probe = document.createElement('div');
    probe.append(fragment.cloneNode(true));

    return probe.textContent?.trim() || probe.querySelector('img, table, ul, ol, video, iframe') ? fragment : null;
  }
}

if (!customElements.get('sk-product-description-tabs')) {
  customElements.define('sk-product-description-tabs', SkProductDescription);
}
